import React from 'react';
import {
  CheckCircle, Star, Trophy, Warning,
} from '@phosphor-icons/react';
import { Badge } from '../../components/ui/badge';
import {
  Card, CardContent, CardHeader, CardTitle,
} from '../../components/ui/card';

const RankBadge = ({ idx, theme }) => {
  const themes = {
    gold: ['bg-amber-100 text-amber-700', 'bg-slate-200 text-slate-600', 'bg-orange-100 text-orange-700'],
    green: ['bg-green-100 text-green-700', 'bg-slate-200 text-slate-600', 'bg-emerald-100 text-emerald-700'],
  };
  return (
    <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold ${themes[theme][idx] || ''}`}>
      {idx + 1}
    </span>
  );
};

const TopAgenciesCard = ({ title, icon: Icon, iconClass, agencies, theme, valueRender }) => (
  <Card className="border-slate-200">
    <CardHeader className="pb-2">
      <CardTitle className="text-base flex items-center gap-2">
        <Icon size={18} className={iconClass} />
        {title}
      </CardTitle>
    </CardHeader>
    <CardContent>
      <div className="space-y-3">
        {agencies.map((agency, idx) => (
          <div
            key={agency.agency_id}
            className="flex items-center justify-between p-2 bg-slate-50 rounded-lg"
          >
            <div className="flex items-center gap-2">
              <RankBadge idx={idx} theme={theme} />
              <span className="font-medium text-sm">{agency.agency_name}</span>
            </div>
            {valueRender(agency)}
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
);

const NeedsAttentionCard = ({ agencies }) => (
  <Card className="border-slate-200">
    <CardHeader className="pb-2">
      <CardTitle className="text-base flex items-center gap-2">
        <Warning size={18} className="text-amber-500" />
        Needs Attention
      </CardTitle>
    </CardHeader>
    <CardContent>
      {agencies.length === 0 ? (
        <div className="text-center py-4 text-slate-500">
          <CheckCircle size={32} className="mx-auto mb-2 text-green-500" />
          <p className="text-sm">All agencies performing well!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {agencies.slice(0, 3).map((agency) => (
            <div
              key={agency.agency_id}
              className="p-2 bg-amber-50 border border-amber-100 rounded-lg"
            >
              <div className="font-medium text-sm">{agency.agency_name}</div>
              <div className="flex gap-2 mt-1 text-xs">
                {parseFloat(agency.collectionRate) < 70 && (
                  <Badge variant="outline" className="border-amber-300 text-amber-700">
                    {agency.collectionRate}% collection
                  </Badge>
                )}
                {parseFloat(agency.denialRate) > 10 && (
                  <Badge variant="outline" className="border-red-300 text-red-700">
                    {agency.denialRate}% denial
                  </Badge>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </CardContent>
  </Card>
);

const PerformanceDistribution = ({ rankedAgencies }) => {
  const buckets = [
    {
      label: 'Excellent (≥80%)', color: 'green',
      count: rankedAgencies.filter((a) => parseFloat(a.collectionRate) >= 80).length,
    },
    {
      label: 'Good (60-79%)', color: 'blue',
      count: rankedAgencies.filter(
        (a) => parseFloat(a.collectionRate) >= 60 && parseFloat(a.collectionRate) < 80,
      ).length,
    },
    {
      label: 'Fair (40-59%)', color: 'amber',
      count: rankedAgencies.filter(
        (a) => parseFloat(a.collectionRate) >= 40 && parseFloat(a.collectionRate) < 60,
      ).length,
    },
    {
      label: 'Needs Work (<40%)', color: 'red',
      count: rankedAgencies.filter((a) => parseFloat(a.collectionRate) < 40).length,
    },
  ];
  return (
    <Card className="border-slate-200 mt-6">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Performance Distribution</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-4 gap-4">
          {buckets.map((b) => (
            <div key={b.label} className={`text-center p-4 bg-${b.color}-50 rounded-lg`}>
              <p className={`text-3xl font-bold text-${b.color}-700`}>{b.count}</p>
              <p className={`text-sm text-${b.color}-600`}>{b.label}</p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export const AgencyRankingsTab = ({ rankedAgencies, slices }) => (
  <>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <TopAgenciesCard
        title="Top Billers"
        icon={Trophy}
        iconClass="text-amber-500"
        agencies={slices.topByBilled}
        theme="gold"
        valueRender={(a) => (
          <span className="font-bold text-indigo-600">
            ${(a.total_billed || 0).toLocaleString()}
          </span>
        )}
      />
      <TopAgenciesCard
        title="Best Collection Rates"
        icon={Star}
        iconClass="text-green-500"
        agencies={slices.topByRate}
        theme="green"
        valueRender={(a) => (
          <Badge className="bg-green-100 text-green-700">{a.collectionRate}%</Badge>
        )}
      />
      <NeedsAttentionCard agencies={slices.needsAttention} />
    </div>
    <PerformanceDistribution rankedAgencies={rankedAgencies} />
  </>
);

export default AgencyRankingsTab;
